import { type FC, ComponentProps, useState } from 'react';
import { Select, Form, InputNumber, Divider, Spin, Tag } from 'antd';
import { Space, Text, Button, DatePicker } from '@g2u/ui-kit';
import { Big } from 'big.js';
import cx from 'classnames';

import { JobServiceType, JobServiceTypeLabel, ServiceProduct, type WithOptionalDataTest } from 'types';
import { AddServiceModal } from '..';
import styles from './service-list.module.scss';
import { calculateTotalHours } from 'helpers/hooks/useTotalHours';
import { useProductList } from 'helpers/hooks/useProductList';

type SelectedProduct = Partial<ServiceProduct> & { productId: ServiceProduct['productId'] };

type Props = {
  customerId: number;
  serviceId?: string;
  isRequired?: boolean;
  totalPrice: string;
  onSelect: (products: SelectedProduct[]) => void;
  selectedProducts?: SelectedProduct[];
  totalHours: number;
  isGeekCover: boolean;
  isClosed: boolean;
  serviceDate: Date | null;
  serviceType: JobServiceType;
} & WithOptionalDataTest;

export const ServiceList: FC<Props> = (props) => {
  const {
    onSelect,
    isRequired,
    totalPrice,
    customerId,
    serviceId,
    selectedProducts,
    totalHours,
    isGeekCover,
    isClosed,
    serviceDate,
    serviceType,
  } = props;
  const [editingProductIndex, setEditingProductIndex] = useState<false | number | null>(false);

  const [productList, isProductsLoading] = useProductList(customerId, serviceId);

  const products = productList?.map((product) => ({
    label: product.name,
    value: product.ID,
    details: product,
  }));

  const handleAddProduct: ComponentProps<typeof AddServiceModal>['onOk'] = (product) => {
    const newProduct: SelectedProduct = {
      id: product.id,
      productId: product.productId,
      quantity: product.qty,
      price: parseFloat(product.details.product?.sub_total || '0'),
      completionDate: product.completionDate,
    };

    const existingItem = (selectedProducts || []).findIndex((p) => p.productId === newProduct.productId);

    const newSelection =
      existingItem === -1
        ? [...(selectedProducts || []), newProduct]
        : selectedProducts!.toSpliced(existingItem, 1, newProduct);

    onSelect(newSelection);
    setEditingProductIndex(false);
  };

  const handleRemoveProduct = (index: number) => {
    selectedProducts && onSelect(selectedProducts.toSpliced(index, 1));
  };

    return (
      <Space.$ direction={Space.Direction.Vertical} className={styles.serviceList}>
        <Text.$ strong size={Text.Size.LG} className={styles.serviceList__title}>
          Services and Fees
        </Text.$>
        {isProductsLoading ? (
          <Space.$ align={Space.Align.Center} direction={Space.Direction.Vertical}>
            <Spin />
          </Space.$>
        ) : (
          <>
            <Tag color="blue">{JobServiceTypeLabel[serviceType]}</Tag>
            <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Small} fullWidth>
              <Form.Item name="products" required rules={isClosed ? [] : [{ required: true }]}>
                <div>
                  {selectedProducts?.map((selectedProduct, index) => (
                    <div className={styles.serviceList__item} key={index}>
                      <Select
                        disabled
                        value={{
                          value: selectedProduct.productId,
                          label:
                            products?.find((p) => p.value === selectedProduct.productId)?.label ||
                            selectedProduct.product?.description,
                        }}
                        style={{ width: '100%', marginRight: 8 }}
                        placeholder="Select a product"
                        optionFilterProp="children"
                        className={cx(styles.serviceList__select, isRequired && styles.serviceList__select_required)}
                        showSearch
                      ></Select>
                      <InputNumber
                        disabled
                        value={selectedProduct.quantity}
                        min={1}
                        width={55}
                        className={cx(styles.serviceList__qty, isRequired && styles.serviceList__qty_required)}
                      />
                      <DatePicker.$
                        disabled
                        // FIXME: UI Kit should allow this to match the forms field definition
                        // @ts-expect-error Type 'Date' is not assignable to type 'string'.
                        value={selectedProduct.completionDate ?? serviceDate ?? new Date()}
                        allowClear={false}
                        className={styles.serviceList__datepicker}
                      />
                      <div className={styles.serviceList__price}>
                        <Text.$ strong size={Text.Size.SM} type="secondary">
                          {`${calculateTotalHours([
                            {
                              qty: selectedProduct.quantity?.toString(),
                              details: {
                                product: productList?.find((p) => p.ID === selectedProduct.productId),
                              },
                            },
                          ])} hours`}
                        </Text.$>
                        <Text.$>
                          {' $'}
                          {Big(
                            productList?.find((p) => p.ID === selectedProduct.productId)?.total ||
                              selectedProduct.product?.total ||
                              0,
                          )
                            .times(selectedProduct.quantity || 0)
                            .toFixed(2)}
                          <small> inc GST</small>
                        </Text.$>
                        <Space.$ justify={Space.Justify.End}>
                          {productList?.find((p) => p.ID === selectedProduct.productId)?.duration && (
                            <Button.$
                              dataTestId="edit-product-1"
                              size={Button.Size.Small}
                              type={Button.Type.Link}
                              className={styles.serviceList__link}
                              onClick={() => {
                                setEditingProductIndex(index);
                              }}
                            >
                              Edit
                            </Button.$>
                          )}
                          <Button.$
                            dataTestId="remove-product-1"
                            size={Button.Size.Small}
                            type={Button.Type.Link}
                            className={styles.serviceList__link}
                            onClick={() => {
                              handleRemoveProduct(index);
                            }}
                          >
                            Remove
                          </Button.$>
                        </Space.$>
                      </div>
                    </div>
                  ))}

                <Button.$
                  type={Button.Type.Dashed}
                  block
                  dataTestId="add-new-product"
                  className={styles.serviceList__action}
                  onClick={() => setEditingProductIndex(null)}
                  style={{ marginTop: 8 }}
                >
                  + Add New
                </Button.$>
              </div>
            </Form.Item>
          </Space.$>

          {selectedProducts && selectedProducts.filter((prod) => prod.id).length > 0 && (
            <>
              <Divider />
              <div className={styles.serviceList__total}>
                <Text.$ size={Text.Size.SM}>
                  Total <br />
                  <b>{selectedProducts.filter((prod) => prod.id).length}</b> items
                </Text.$>
                <div className={styles.serviceList__totalHours}>
                  <Text.$ strong size={Text.Size.MD} type="secondary">
                    {`${totalHours} hours`}
                  </Text.$>
                  <Text.$ strong>
                    ${totalPrice} <small>inc GST</small>
                  </Text.$>
                </div>
              </div>
            </>
          )}

          {editingProductIndex !== false && (
            <AddServiceModal
              onOk={(product) => handleAddProduct(product)}
              onCancel={() => setEditingProductIndex(false)}
              isOpen
              defaultCompletionDate={serviceDate ?? selectedProducts?.[0]?.completionDate ?? new Date()}
              selectedProducts={selectedProducts}
              currentProductId={editingProductIndex}
              productList={productList}
              isGeekcoverMember={isGeekCover}
            />
          )}
        </>
      )}
    </Space.$>
  );
};
